// webapp/src/lib/ipscScoreQueue.js
//
// Durable, ORDERED op log for IPSC scoresheets (dynamisk skyting). Unlike the
// NSF cell queue (conventionScoreQueue.js, "desired-state upsert"), an IPSC
// sheet is a sequence of operations that must reach the server in order and
// must never be collapsed: score → approve → edit (with reason). Every op is
// persisted to IndexedDB the instant it is entered and replayed on reconnect /
// reload. Optimistic versioning: each write carries `baseVersion`; a 409 parks
// the sheet as 'conflict' for the RO to resolve, a 423 (results locked) parks it
// as 'held' until an official unlocks and the RO retries.
//
// Entry (one per op), keyPath 'opKey':
//   { opKey: `${sheetKey}#${seq}`, sheetKey, seq, kind: 'score'|'approve'|'edit',
//     convention, participant, division, stand, stage, payload, baseVersion|null,
//     scoredBy, scoredByName, deviceId, scoredDate, status, attempts, lastAttemptAt,
//     savingAt, lastError, serverId|null, version|null, serverRow|null, createdAt }
//
// Store-independent (imports no MobX stores) so it is unit-testable.

import util from 'preact-util';
import { tx } from './offlineStorage';
import {
    backoffMs, createKeyLock, isPermanent, isSuccess, toPlain, makeMemoryStorage, SAVING_LEASE_MS,
} from './offlineQueueCore';

const STORE = 'pendingIpscOps';
export const OP_KINDS = ['score', 'approve', 'edit'];

export function sheetKey({ convention, participant, division, stand }) {
    return `${convention}-${participant}-${division}-${stand}`;
}

function makeIdbStorage() {
    return {
        getAll: () => tx('readonly', STORE, store => store.getAll()),
        put: (entry) => tx('readwrite', STORE, store => store.put(entry)),
        delete: (key) => tx('readwrite', STORE, store => store.delete(key)),
        get: (key) => tx('readonly', STORE, store => store.get(key)),
    };
}

let _defaultStorage = null;
function defaultStorage() {
    if (!_defaultStorage) _defaultStorage = makeIdbStorage();
    return _defaultStorage;
}

export function _makeMemoryStorage() {
    return makeMemoryStorage('opKey');
}

const _subs = new Set();
export function subscribe(cb) { _subs.add(cb); return () => _subs.delete(cb); }
function notify() { _subs.forEach(cb => { try { cb(); } catch (e) { /* ignore */ } }); }

// One lock per SHEET (not per op): every read-modify-write of a sheet's ops runs
// serialised so enqueue / claim / complete never interleave on the same sheet.
const withSheetLock = createKeyLock();

async function readAll(storage) {
    try { const all = await storage.getAll(); return Array.isArray(all) ? all : []; } catch (e) { return []; }
}

function opsOf(all, key) {
    return all.filter(e => e && e.sheetKey === key).sort((a, b) => a.seq - b.seq);
}

// What the sheet currently knows from the server: the last synced op's id/version.
function learned(ops) {
    let serverId = null;
    let version = null;
    ops.forEach((op) => {
        if (op.status === 'synced') {
            if (op.serverId != null) serverId = op.serverId;
            if (op.version != null) version = op.version;
        }
    });
    return { serverId, version };
}

export async function enqueue(op, storage = defaultStorage()) {
    const {
        kind, convention, participant, division, stand, stage = '', payload = {}, user = {}, deviceId = '', baseVersion = null,
    } = op;
    if (!OP_KINDS.includes(kind)) throw new Error(`ipscScoreQueue: unknown op kind ${kind}`);
    const key = sheetKey({ convention, participant, division, stand });
    return withSheetLock(key, async () => {
        const ops = opsOf(await readAll(storage), key);
        const seq = ops.length ? ops[ops.length - 1].seq + 1 : 1;
        const name = `${user.firstname || ''} ${user.lastname || ''}`.trim();
        const entry = toPlain({
            opKey: `${key}#${seq}`,
            sheetKey: key,
            seq,
            kind,
            convention,
            participant,
            division,
            stand,
            stage,
            payload,
            baseVersion: baseVersion == null ? null : baseVersion,
            scoredBy: user.id != null ? user.id : null,
            scoredByName: name || null,
            deviceId,
            scoredDate: new Date().toISOString(),
            status: 'pending',
            attempts: 0,
            lastAttemptAt: null,
            savingAt: null,
            lastError: null,
            serverId: null,
            version: null,
            serverRow: null,
            createdAt: Date.now(),
        });
        try { await storage.put(entry); } catch (e) { /* best-effort */ }
        notify();
        return entry;
    });
}

export async function getAll(storage = defaultStorage()) {
    return readAll(storage);
}

/**
 * Aggregate view of one sheet for the UI:
 *   status: 'none' | 'pending' | 'saving' | 'synced' | 'held' | 'conflict' | 'failed'
 */
export async function getSheetState(key, storage = defaultStorage()) {
    const ops = opsOf(await readAll(storage), key);
    return sheetStateFromOps(ops);
}

export function sheetStateFromOps(ops) {
    if (!ops.length) {
        return {
            status: 'none', serverId: null, version: null, lastError: null, serverRow: null, ops: [],
        };
    }
    const { serverId, version } = learned(ops);
    const blocked = ops.find(op => ['conflict', 'held', 'failed'].includes(op.status));
    let status = 'synced';
    if (blocked) {
        status = blocked.status;
    } else if (ops.some(op => op.status === 'saving')) {
        status = 'saving';
    } else if (ops.some(op => op.status === 'pending')) {
        status = 'pending';
    }
    return {
        status,
        serverId,
        version,
        lastError: blocked ? blocked.lastError : null,
        serverRow: blocked && blocked.status === 'conflict' ? blocked.serverRow : null,
        ops,
    };
}

// Group every sheet's state in one pass (for the RO list).
export async function getAllSheetStates(storage = defaultStorage()) {
    const all = await readAll(storage);
    const byKey = {};
    all.forEach((e) => {
        if (!e || !e.sheetKey) return;
        if (!byKey[e.sheetKey]) byKey[e.sheetKey] = [];
        byKey[e.sheetKey].push(e);
    });
    const out = {};
    Object.keys(byKey).forEach((key) => {
        out[key] = sheetStateFromOps(byKey[key].sort((a, b) => a.seq - b.seq));
    });
    return out;
}

async function updateOps(key, storage, mutate) {
    return withSheetLock(key, async () => {
        const ops = opsOf(await readAll(storage), key);
        const changed = mutate(ops) || [];
        // eslint-disable-next-line no-restricted-syntax
        for (const op of changed) {
            // eslint-disable-next-line no-await-in-loop
            try { await storage.put(op); } catch (e) { /* ignore */ }
        }
        notify();
        return changed;
    });
}

// held / failed → pending (attempts reset) so the next drain sends them again.
export async function retrySheet(key, storage = defaultStorage()) {
    return updateOps(key, storage, ops => ops
        .filter(op => op.status === 'held' || op.status === 'failed')
        .map(op => ({
            ...op, status: 'pending', attempts: 0, lastAttemptAt: null, savingAt: null, lastError: null,
        })));
}

// The RO chose to re-send on top of the server's version (or to re-enter — the
// page enqueues a fresh 'edit' after calling this with the server's version).
export async function resolveConflict(key, { baseVersion, serverId = null }, storage = defaultStorage()) {
    return updateOps(key, storage, (ops) => {
        const idx = ops.findIndex(op => op.status === 'conflict');
        if (idx < 0) return [];
        const op = ops[idx];
        return [{
            ...op,
            status: 'pending',
            baseVersion,
            serverId: serverId != null ? serverId : op.serverId,
            attempts: 0,
            lastAttemptAt: null,
            savingAt: null,
            lastError: null,
            serverRow: null,
        }];
    });
}

// Drop the pending ops of a sheet (RO discards their local entry after a conflict).
export async function discardPending(key, storage = defaultStorage()) {
    return withSheetLock(key, async () => {
        const ops = opsOf(await readAll(storage), key);
        // eslint-disable-next-line no-restricted-syntax
        for (const op of ops) {
            if (op.status !== 'synced') {
                // eslint-disable-next-line no-await-in-loop
                try { await storage.delete(op.opKey); } catch (e) { /* ignore */ }
            }
        }
        notify();
    });
}

// Remove synced ops except the latest one, which keeps serverId/version for the sheet.
export async function clearSynced(key, storage = defaultStorage()) {
    return withSheetLock(key, async () => {
        const ops = opsOf(await readAll(storage), key);
        const synced = ops.filter(op => op.status === 'synced');
        const keep = synced.length ? synced[synced.length - 1].opKey : null;
        // eslint-disable-next-line no-restricted-syntax
        for (const op of synced) {
            if (op.opKey !== keep) {
                // eslint-disable-next-line no-await-in-loop
                try { await storage.delete(op.opKey); } catch (e) { /* ignore */ }
            }
        }
        notify();
    });
}

// What the RO page should base a NEW op on: the server row's version only when
// the queue holds nothing for the sheet. Once ops exist, null → the drain uses
// the version learned from the previous op, so two offline edits in a row never
// collide with each other (reviewer finding: false "Konflikt").
export function baseVersionForEnqueue({ sheetState, serverSheet } = {}) {
    const hasOps = !!(sheetState && sheetState.status !== 'none' && (sheetState.ops || []).length > 0);
    if (hasOps) return null;
    return serverSheet && serverSheet.version != null ? serverSheet.version : null;
}

// The sheet as this device last entered it (pending or synced), and whether an
// approve op exists — used to prefill the RO page while ops wait offline.
export function latestLocalSheet(ops = []) {
    const sorted = [...ops].sort((a, b) => a.seq - b.seq);
    let payload = null;
    let approved = false;
    sorted.forEach((op) => {
        if (op.kind === 'score' || op.kind === 'edit') payload = op.payload || null;
        if (op.kind === 'approve') approved = true;
    });
    return { payload, approved };
}

function extractRow(resp) {
    return resp && resp.data && resp.data.conventionipscscore ? resp.data.conventionipscscore : null;
}

// Default transport. Tests inject `sendFn(op, ctx)` instead.
// ctx = { serverId, version, baseVersion } learned from the sheet's synced ops.
async function defaultSendFn(op, ctx) {
    if (op.kind === 'approve') {
        return util.fetchApi(`/api/conventionipscscores/${ctx.serverId}/approve`, { method: 'POST' }, {
            id: ctx.serverId,
            approvedByName: op.payload.approvedByName,
            version: ctx.version,
        });
    }
    const body = {
        convention: op.convention,
        participant: op.participant,
        division: op.division,
        stand: op.stand,
        stage: op.stage,
        ...op.payload,
        baseVersion: ctx.baseVersion,
        scoredBy: op.scoredBy,
        scoredByName: op.scoredByName,
        scoredDate: op.scoredDate,
        deviceId: op.deviceId,
    };
    if (ctx.serverId) {
        return util.fetchApi(`/api/conventionipscscores/${ctx.serverId}`, { method: 'PATCH' }, { id: ctx.serverId, ...body });
    }
    return util.fetchApi('/api/conventionipscscores/', { method: 'POST' }, body);
}

let _draining = null;
let _drainAgain = false;

export async function drain(opts = {}) {
    if (_draining) {
        _drainAgain = true;
        return _draining;
    }
    _draining = (async () => {
        let counters;
        do {
            _drainAgain = false;
            // eslint-disable-next-line no-await-in-loop
            counters = await drainOnce(opts);
        } while (_drainAgain);
        return counters;
    })();
    try {
        return await _draining;
    } finally {
        _draining = null;
        if (_drainAgain) {
            _drainAgain = false;
            Promise.resolve().then(() => drain(opts)).catch(() => {});
        }
    }
}

async function drainOnce(opts = {}) {
    const storage = opts.storage || defaultStorage();
    const sendFn = opts.sendFn || defaultSendFn;
    const onSynced = opts.onSynced || (() => {});
    const now = (opts.now || Date.now)();
    const counters = {
        ok: 0, held: 0, conflict: 0, failed: 0, transient: 0, skipped: 0,
    };

    const all = await readAll(storage);
    const keys = Array.from(new Set(all.map(e => e && e.sheetKey).filter(Boolean)));

    // eslint-disable-next-line no-restricted-syntax
    for (const key of keys) {
        // Ops of one sheet go strictly in seq order; the first op that cannot be
        // sent now (blocked, leased, backing off) stops the sheet for this pass.
        let stop = false;
        while (!stop) {
            // CLAIM (atomic per sheet): find the first unsynced op and lease it.
            // eslint-disable-next-line no-await-in-loop
            const claim = await withSheetLock(key, async () => {
                const ops = opsOf(await readAll(storage), key);
                const next = ops.find(op => op.status !== 'synced');
                if (!next) return { done: true };
                if (['conflict', 'held', 'failed'].includes(next.status)) return { skip: next.status };
                if (next.status === 'saving' && next.savingAt && now - next.savingAt < SAVING_LEASE_MS) return { skip: 'inflight' };
                if (next.attempts > 0 && next.lastAttemptAt && now - next.lastAttemptAt < backoffMs(next.attempts)) return { skip: 'backoff' };
                const ctx = learned(ops);
                if (next.kind === 'approve' && ctx.serverId == null) return { skip: 'noServerId' };
                const saving = { ...next, status: 'saving', savingAt: now };
                try { await storage.put(saving); } catch (e) { /* ignore */ }
                notify();
                return {
                    op: saving,
                    ctx: {
                        serverId: next.serverId != null ? next.serverId : ctx.serverId,
                        version: ctx.version,
                        baseVersion: next.baseVersion != null ? next.baseVersion : (ctx.version != null ? ctx.version : 0),
                    },
                };
            });
            if (claim.done) break;
            if (claim.skip) { counters.skipped += 1; break; }

            let resp;
            try {
                // eslint-disable-next-line no-await-in-loop
                resp = await sendFn({ ...claim.op, payload: { ...claim.op.payload } }, claim.ctx);
            } catch (e) {
                resp = null;
            }

            // COMPLETE (atomic per sheet).
            // eslint-disable-next-line no-await-in-loop
            const outcome = await withSheetLock(key, async () => {
                let fresh = null;
                try { fresh = await storage.get(claim.op.opKey); } catch (e) { fresh = null; }
                if (!fresh) return 'gone';
                const row = extractRow(resp);
                let updated;
                let kind;
                if (isSuccess(resp)) {
                    updated = {
                        ...fresh,
                        status: 'synced',
                        savingAt: null,
                        lastError: null,
                        serverId: row && row.id != null ? row.id : (claim.ctx.serverId != null ? claim.ctx.serverId : fresh.serverId),
                        version: row && row.version != null ? row.version : claim.ctx.version,
                    };
                    kind = 'ok';
                } else if (resp && resp.status === 423) {
                    updated = {
                        ...fresh, status: 'held', savingAt: null, lastAttemptAt: now, lastError: { status: 423, message: resp.message || 'locked' },
                    };
                    kind = 'held';
                } else if (resp && resp.status === 409) {
                    updated = {
                        ...fresh,
                        status: 'conflict',
                        savingAt: null,
                        lastAttemptAt: now,
                        serverRow: row,
                        serverId: row && row.id != null ? row.id : fresh.serverId,
                        lastError: { status: 409, message: resp.message || 'conflict' },
                    };
                    kind = 'conflict';
                } else if (isPermanent(resp)) {
                    updated = {
                        ...fresh,
                        status: 'failed',
                        savingAt: null,
                        attempts: (fresh.attempts || 0) + 1,
                        lastAttemptAt: now,
                        lastError: { status: resp.status, message: resp.message || '', errors: resp.errors || null },
                    };
                    kind = 'failed';
                } else {
                    updated = {
                        ...fresh,
                        status: 'pending',
                        savingAt: null,
                        attempts: (fresh.attempts || 0) + 1,
                        lastAttemptAt: now,
                        lastError: { status: (resp && resp.status) || 0, message: (resp && resp.message) || 'network' },
                    };
                    kind = 'transient';
                }
                try { await storage.put(updated); } catch (e) { /* ignore */ }
                notify();
                return kind;
            });

            if (outcome === 'ok') {
                counters.ok += 1;
                try { onSynced(claim.op, resp); } catch (e) { /* ignore */ }
            } else {
                if (outcome === 'held') counters.held += 1;
                else if (outcome === 'conflict') counters.conflict += 1;
                else if (outcome === 'failed') counters.failed += 1;
                else if (outcome === 'transient') counters.transient += 1;
                stop = true;
            }
        }
    }
    return counters;
}
